import { useMemo, useState } from 'react';
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';
import { searchInSearchOptions } from '../tableSearch/tableSearchUseCase';

/**
 * Flatten grouped options for search: each option gets id, lib, optGroup, color
 * @param {Array<{ label: string, options: Array<{ value: string, label: string, color?: string }> }>} optionsGrouped
 * @returns {Array<{ id: string, lib: string, optGroup: string, color?: string }>}
 */
function flattenOptionsForSearch(optionsGrouped) {
  if (!optionsGrouped?.length) return [];
  return optionsGrouped.flatMap((group) => group.options.map((option) => ({
    id: String(option.value),
    lib: option.label || '',
    optGroup: group.label || '',
    color: option.color,
  })));
}

/**
 * Hook to manage the select multiple checkbox list
 * @param {useSelectMultipleCheckboxListProps} props
 * @returns {{
 *  search: string,
 *  setSearch: (search: string) => void,
 *  popoverOpen: boolean,
 *  setPopoverOpen: (popoverOpen: boolean) => void,
 *  optionsLabelMap: Record<string, { label: string, color?: string }>,
 *  filteredOptionsGrouped: { lib: string; options: any[]; }[],
 *  areAllVisibleSelected: boolean,
 *  selectAllVisibleIndeterminate: boolean,
 *  handleSelectAllVisible: () => void,
 *  handleSelectGroup: (groupOptions: Array<{ value: string, label: string, color?: string }>) => void,
 *  areAllGroupSelected: (groupOptions: Array<{ value: string, label: string, color?: string }>) => boolean,
 *  isGroupOptionsIndeterminate: (groupOptions: Array<{ value: string, label: string, color?: string }>) => boolean,
 *  handleToggleOption: (optionId: string) => void,
 *  selectedSet: Set<string>,
 * }}
 */
const useSelectMultipleCheckboxList = (props) => {
  const { optionsGrouped, value, onChange } = props;
  const [search, setSearch] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);

  const optionsFlat = useMemo(() => flattenOptionsForSearch(optionsGrouped), [optionsGrouped]);

  const optionsLabelMap = useMemo(() => {
    const map = /** @type {Record<string, { label: string, color?: string }>} */ ({});
    optionsGrouped?.forEach((group) => {
      group.options.forEach((option) => {
        map[String(option.value)] = { label: option.label || '', color: option.color };
      });
    });
    return map;
  }, [optionsGrouped]);

  const filteredOptionsFlat = useMemo(
    () => searchInSearchOptions(optionsFlat, search),
    [optionsFlat, search],
  );

  const filteredOptionsGrouped = useMemo(
    () => groupOptionsByOptGroup(filteredOptionsFlat),
    [filteredOptionsFlat],
  );

  const selectedSet = useMemo(() => new Set((value || []).map((id) => String(id))), [value]);

  const areAllVisibleSelected = filteredOptionsFlat.length > 0
  && filteredOptionsFlat.every((opt) => selectedSet.has(String(opt.id)));

  const selectAllVisibleIndeterminate = filteredOptionsFlat.length > 0
  && !areAllVisibleSelected
  && filteredOptionsFlat.some((opt) => selectedSet.has(String(opt.id)));

  const handleSelectAllVisible = () => {
    const filteredIds = filteredOptionsFlat.map((o) => o.id);
    if (areAllVisibleSelected) {
      const removeSet = new Set(filteredIds);
      onChange((value || []).filter((id) => !removeSet.has(String(id))));
    } else {
      const combined = new Set([...(value || []).map(String), ...filteredIds]);
      // Use Array.from() instead of [...combined] so iteration works without downlevelIteration / ES2015+ target
      onChange(Array.from(combined));
    }
  };

  /**
   * Handle select group
   * @param {selectMultipleCheckboxGroupOptions[]} groupOptions
   * @returns {void}
   */
  const handleSelectGroup = (groupOptions) => {
    const ids = groupOptions.map((o) => o.id);
    const allSelected = ids.every((id) => selectedSet.has(String(id)));
    if (allSelected) {
      const removeSet = new Set(ids);
      onChange((value || []).filter((id) => !removeSet.has(String(id))));
    } else {
      const combined = new Set([...(value || []).map(String), ...ids]);
      // Use Array.from() instead of [...combined] so iteration works without downlevelIteration / ES2015+ target
      onChange(Array.from(combined));
    }
  };

  /**
   * Check if all group options are selected
   * @param {selectMultipleCheckboxGroupOptions[]} groupOptions
   * @returns {boolean}
   */
  const areAllGroupSelected = (groupOptions) => groupOptions.length > 0 && groupOptions.every((opt) => selectedSet.has(String(opt.id)));

  /**
   * @param {selectMultipleCheckboxGroupOptions[]} groupOptions
   * @returns {boolean}
   */
  const isGroupOptionsIndeterminate = (groupOptions) => {
    if (!groupOptions.length) return false;
    const any = groupOptions.some((opt) => selectedSet.has(String(opt.id)));
    return any && !areAllGroupSelected(groupOptions);
  };

  /**
   * Handle toggle option
   * @param {string} optionId
   * @returns {void}
   */
  const handleToggleOption = (optionId) => {
    const idStr = String(optionId);
    if (selectedSet.has(idStr)) {
      onChange((value || []).filter((id) => String(id) !== idStr));
    } else {
      onChange([...(value || []), idStr]);
    }
  };

  return {
    search,
    setSearch,
    popoverOpen,
    setPopoverOpen,
    optionsLabelMap,
    filteredOptionsGrouped,
    areAllVisibleSelected,
    selectAllVisibleIndeterminate,
    handleSelectAllVisible,
    handleSelectGroup,
    areAllGroupSelected,
    isGroupOptionsIndeterminate,
    handleToggleOption,
    selectedSet,
  };
};

export default useSelectMultipleCheckboxList;
